import { isBoolean, isNumber, isString } from 'lodash-es';
import { IUpdateModelParams } from '../data/model/model';
import { IPoint2d } from '../math/IPoint2d';
import { Point2dUtils } from '../math/Point2dUtils';
import { globalStores } from '../stores';
import { EEntityType } from '../types';
import FurnitureModel2d from './2d/furnitureModel';
import FurnitureModel3d from './3d/furnitureModel';
import BaseEntity from './BaseEntity';
import { EEntityStatus } from '../types/entity';
import type { IFurniture } from '@/editor/data/model/furniture';
import { ILibraryModel } from '@/service/library/types';

class FurnitureEntity extends BaseEntity<EEntityType.Furniture> {
  protected override _model2d: FurnitureModel2d;
  protected override _model3d: FurnitureModel3d;
  private _modelInfo: ILibraryModel | null = null;
  private _center: IPoint2d = { x: 0, y: 0 };

  private _svgUrl: string = '';
  private _width: number = 0;
  private _thickness: number = 0;
  private _height: number = 0;
  private _rotation: number = 0;
  private _offGround: number = 0;
  private _status: EEntityStatus = EEntityStatus.normal;
  private _originalWorkPoints: IPoint2d[] = [];
  private _workPoints: IPoint2d[] = [];
  private _workPointVisible = false;
  private _baseModelWidth = 0;
  private _baseModelThickness = 0;

  constructor(options: { id: string; visible?: boolean }) {
    super({ ...options, type: EEntityType.Furniture });

    this._model2d = new FurnitureModel2d(options);
    this._model3d = new FurnitureModel3d(options.id);
    if (options.visible !== undefined) {
      this._visible = options.visible;
    }
  }

  get data(): IFurniture {
    return {
      modelId: this._modelId,
      id: this._id,
      center: this._center,
      offGround: this._offGround,
      width: this._width,
      thickness: this._thickness,
      height: this._height,
      rotation: this._rotation
    };
  }

  get model2d() {
    return this._model2d;
  }

  get svgUrl() {
    return this._svgUrl;
  }

  get workPointVisible() {
    return this._workPointVisible;
  }

  get visible() {
    return this._visible;
  }

  setVisible(visible: boolean) {
    if (visible === this._visible) return;
    this._visible = visible;

    if (visible) {
      this.update({ forceUpdate: true });
    } else {
      this.update({});
      this._model2d.hide();
    }
  }

  setWorkPointVisible(visible: boolean) {
    this.update({ workPointsVisible: visible });
  }

  override set selected(value: boolean) {
    this.update({ selected: value });
  }

  override get selected() {
    return this._selected;
  }

  override update(data: Partial<IUpdateModelParams> & { forceUpdate?: boolean }) {
    const {
      center,
      width,
      thickness,
      height,
      modelId,
      forceUpdate,
      rotation,
      selected,
      status,
      offGround,
      workPointsVisible: workPointsVisible
    } = data;

    let needDestroyUpdate = forceUpdate || false;
    let noDestroyUpdate = false;
    let dimensionsChanged = false;
    let workPointSourceChanged = false;

    if (isString(modelId) && this._modelId !== modelId) {
      this._modelId = modelId;
      this._modelInfo = globalStores.modelStore.getModel(this._modelId);

      needDestroyUpdate = true;

      this._svgUrl = this._modelInfo?.model2d || '';
      this._originalWorkPoints = this._modelInfo?.properties?.wp ?? [];

      this._baseModelWidth = Number(this._modelInfo?.properties?.width) || 0;
      this._baseModelThickness = Number(this._modelInfo?.properties?.thickness) || 0;
      workPointSourceChanged = true;
    }

    if (center && !Point2dUtils.equals(this._center, center)) {
      this._center = center;
      noDestroyUpdate = true;
    }

    if (isNumber(width) && this._width !== width) {
      this._width = width;
      noDestroyUpdate = true;
      dimensionsChanged = true;
    }

    if (isNumber(thickness) && this._thickness !== thickness) {
      this._thickness = thickness;
      noDestroyUpdate = true;
      dimensionsChanged = true;
    }

    if (isNumber(offGround) && this._offGround !== offGround) {
      this._offGround = offGround;
      noDestroyUpdate = true;
    }

    if (isNumber(height) && this._height !== height) {
      this._height = height;
      noDestroyUpdate = true;
    }

    if (isNumber(rotation) && this._rotation !== rotation) {
      this._rotation = rotation;
      noDestroyUpdate = true;
    }

    if (isBoolean(selected) && selected !== this._selected) {
      this._selected = selected;
      noDestroyUpdate = true;
    }

    if (isString(status) && status !== this._status) {
      this._status = status;
      noDestroyUpdate = true;
    }

    if (isBoolean(workPointsVisible) && workPointsVisible !== this._workPointVisible) {
      this._workPointVisible = workPointsVisible;
      noDestroyUpdate = true;
    }

    if (workPointSourceChanged || dimensionsChanged) {
      const recomputed = this.recomputeWorkPoints();
      if (recomputed) {
        noDestroyUpdate = true;
      }
    }

    if (!this._visible) {
      this.clear();
      return;
    }

    if (needDestroyUpdate) {
      this._model2d.update({
        center: this._center,
        model2d: this._svgUrl,
        modelId: this._modelId,
        width: this._width,
        thickness: this._thickness,
        rotation: this._rotation,
        status: this._status,
        selected: this._selected,
        workPoints: this._workPoints,
        workPointsVisible: this._workPointVisible
      });
      this._model3d.update({
        center: this._center,
        width: this._width,
        thickness: this._thickness,
        rotation: this._rotation,
        height: this._height,
        offGround: this._offGround,
        modelId: this._modelId,
        status: this._status,
        selected: this._selected,
        flip: false
      });
    } else if (noDestroyUpdate) {
      this._model2d.updateWithoutDestroy({
        center: this._center,
        rotation: this._rotation,
        status: this._status,
        width: this._width,
        thickness: this._thickness,
        workPoints: this._workPoints,
        workPointsVisible: this._workPointVisible
      });
      this._model3d.updateWithoutDestroy({
        center: this._center,
        rotation: this._rotation,
        width: this._width,
        thickness: this._thickness,
        height: this._height,
        offGround: this._offGround,
        flip: false
      });
    }
  }

  clear() {
    this._model2d.clear();
    this._model3d.clearModel();
  }

  private recomputeWorkPoints() {
    if (!this._originalWorkPoints.length) {
      this._workPoints = [];
      return true;
    }

    if (!this._width || !this._thickness) {
      this._workPoints = this._originalWorkPoints.map((point) => point);
      return true;
    }

    const baseWidth = this._baseModelWidth || this._width;
    const baseThickness = this._baseModelThickness || this._thickness;
    if (!baseWidth || !baseThickness) {
      this._workPoints = this._originalWorkPoints.map((point) => point);
      return true;
    }

    const scaleX = this._width / baseWidth;
    const scaleY = this._thickness / baseThickness;

    this._workPoints = this._originalWorkPoints.map((point) => {
      const { x = 0, y = 0 } = point;
      return { x: x * scaleX, y: y * scaleY };
    });

    return true;
  }
}

export default FurnitureEntity;
